'use client';

import { useTheme } from 'next-themes';
import { useEffect, useState } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import { appHasMounted, markAppMounted } from '@/hooks/appMounted';
import { theme } from '@/lib/ui/theme';

interface LanguagesSectionProps {
  isLoading?: boolean;
}

const languages = ['Go', 'Python', 'TypeScript', 'Rust'];

export function LanguagesSection({ isLoading = false }: LanguagesSectionProps) {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(appHasMounted());

  useEffect(() => {
    markAppMounted();
    setMounted(true);
  }, []);

  const isDark = mounted && resolvedTheme === 'dark';

  if (isLoading || !mounted) {
    return (
      <section className="px-6 py-12 md:px-16 md:py-16">
        <div className="mx-auto max-w-[1000px] text-center">
          <Skeleton className="mx-auto mb-4 h-9 w-[45%]" />
          <Skeleton className="mx-auto mb-8 h-5 w-[60%]" />
          <div className="flex flex-wrap justify-center gap-3">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-10 w-28 rounded-lg" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="px-6 py-12 md:px-16 md:py-16">
      <div className="mx-auto max-w-[1000px] text-center">
        <h2
          className="text-gradient-hero mb-6 text-2xl font-bold tracking-tight md:text-3xl"
          style={{ fontFamily: theme.fonts.display }}
        >
          One bar. Four languages.
        </h2>
        <p
          className="text-foreground/70 mx-auto mb-8 max-w-[640px] text-base md:text-lg"
          style={{ fontFamily: theme.fonts.body }}
        >
          Mindrealm runs the same checks and toolchain gates across Go, Python,
          TypeScript, and Rust, so the review stays consistent when your agent
          changes languages.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          {languages.map((language) => (
            <span
              key={language}
              className="rounded-lg px-5 py-2 text-base font-medium md:text-lg"
              style={{
                background: theme.box.purple.bg[isDark ? 'dark' : 'light'],
                border: `1px solid ${theme.box.purple.border[isDark ? 'dark' : 'light']}`,
                fontFamily: theme.fonts.body,
              }}
            >
              {language}
            </span>
          ))}
        </div>
        <p
          className="text-foreground/60 mt-6 text-base md:text-lg"
          style={{ fontFamily: theme.fonts.body }}
        >
          Same depth on architecture, concurrency, reliability, security, and
          performance, no matter the language.
        </p>
      </div>
    </section>
  );
}
